import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { clientsData, CARD_DURATION } from './clientsData';
import { useFadingClients } from './hooks/useFadingClients';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import IncorporationPage from './pages/IncorporationPage';
import AISolutionsPage from './pages/AISolutionsPage';
import B2BSolutionsPage from './pages/B2BSolutionsPage';
import MarketingStrategyPage from './pages/MarketingStrategyPage';
import BrandDesignPage from './pages/BrandDesignPage';
import BusinessConsultingPage from './pages/BusinessConsultingPage';
import BusinessSoftwarePage from './pages/BusinessSoftwarePage';
import PortfolioPage from './pages/PortfolioPage';
import CaseStudyPage from './pages/CaseStudyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PartnersPage from './pages/PartnersPage';
import NotFoundPage from './pages/NotFoundPage';
import { trackPageView } from './lib/analytics';
import { scrollToSection } from './utils/scroll';
import { useParallaxInput } from './hooks/useParallaxInput';
import { I18nProvider } from './i18n/I18nContext';
import { getLanguageFromPath, stripLanguageFromPath } from './i18n/config';
import { Client } from './clientsData';

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (hash) {
      let sectionId: string;
      try {
        sectionId = decodeURIComponent(hash.slice(1));
      } catch {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        return;
      }
      requestAnimationFrame(() => {
        if (document.getElementById(sectionId)) {
          scrollToSection(sectionId);
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [pathname, hash]);

  return null;
}

function LanguageRoutes({ visibleClients }: { visibleClients: Client[] }) {
  const stripped = stripLanguageFromPath(window.location.pathname) || '/';

  return (
    <Routes location={stripped}>
      <Route path="/" element={<HomePage visibleClients={visibleClients} />} />
      <Route path="/services/incorporation" element={<IncorporationPage />} />
      <Route path="/services/ai-solutions" element={<AISolutionsPage />} />
      <Route path="/services/b2b-solutions" element={<B2BSolutionsPage />} />
      <Route path="/services/marketing-strategy" element={<MarketingStrategyPage />} />
      <Route path="/services/brand-design" element={<BrandDesignPage />} />
      <Route path="/services/business-consulting" element={<BusinessConsultingPage />} />
      <Route path="/services/business-software" element={<BusinessSoftwarePage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/case-study/:id" element={<CaseStudyPage />} />
      <Route path="/terms-conditions" element={<TermsConditionsPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const visibleClients = useFadingClients(clientsData, CARD_DURATION);
  const location = useLocation();
  const parallaxRef = useParallaxInput<HTMLDivElement>();

  const initialLang = React.useMemo(() => getLanguageFromPath(window.location.pathname), []);

  React.useEffect(() => {
    trackPageView(`${location.pathname}${location.search}${location.hash}`);
  }, [location.pathname, location.search, location.hash]);

  return (
    <I18nProvider initialLang={initialLang}>
      <div ref={parallaxRef} className="site-atmosphere min-h-screen bg-transparent">
        <ScrollToTop />
        <Toaster position="top-right" />
        <Navigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
        <div key={location.pathname} className="route-stage">
          <LanguageRoutes visibleClients={visibleClients} />
        </div>
        <Footer />
      </div>
    </I18nProvider>
  );
}

export default App;
